import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { submitBooking } from "@/lib/api";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";

const EVENT_TYPES = ["Club Night", "Festival", "Private Event", "Brand / Showcase", "Other"];

const inputCls =
  "bg-transparent border-0 border-b border-white/20 rounded-none focus-visible:ring-0 focus-visible:border-[#FF2A2A] focus-visible:outline-none px-0 py-3 font-mono text-white placeholder:text-[#5A5A5A] h-auto";

export default function Booking() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    event_type: EVENT_TYPES[0],
    event_date: undefined,
    location: "",
    budget: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.event_date || !form.location) {
      toast.error("Missing required fields", {
        description: "Name, email, date and location are required.",
      });
      return;
    }
    setSubmitting(true);
    try {
      await submitBooking({
        ...form,
        event_date:
          form.event_date instanceof Date
            ? form.event_date.toISOString().slice(0, 10)
            : form.event_date,
      });
      setDone(true);
      toast.success("Booking received", {
        description: "We'll be in touch within 48 hours.",
      });
      setForm({
        name: "",
        email: "",
        event_type: EVENT_TYPES[0],
        event_date: undefined,
        location: "",
        budget: "",
        message: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Could not submit. Try again.";
      toast.error("Submission failed", { description: String(msg) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative py-24 sm:py-32 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#FF2A2A]">
            [ 05 / Book ]
          </span>
          <h2 className="mt-4 font-heading font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.9]">
            Bring the<br />
            Disturbance.
          </h2>
          <p className="mt-6 text-[#A8A8A8] leading-relaxed">
            Clubs, festivals, ceremonies, warehouses — tell me the room and the hour.
            All bookings reviewed within 48h.
          </p>

          <div className="mt-10 border-t border-white/10 pt-8 space-y-4 font-mono text-xs tracking-[0.2em] uppercase">
            <div>
              <span className="text-[#8A8A8A]">Agent · </span>
              <span className="text-[#F5F5F5]">contact@diskobhute.fm</span>
            </div>
            <div>
              <span className="text-[#8A8A8A]">Press · </span>
              <span className="text-[#F5F5F5]">press@diskobhute.fm</span>
            </div>
            <div>
              <span className="text-[#8A8A8A]">Loc · </span>
              <span className="text-[#F5F5F5]">Berlin / Bangalore</span>
            </div>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-7">
        <form
          data-testid="booking-form"
          onSubmit={onSubmit}
          className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8 bg-[#0A0A0A] border border-white/10 p-6 sm:p-10"
        >
          <div className="sm:col-span-1">
            <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#8A8A8A]">
              Name *
            </label>
            <Input
              data-testid="booking-input-name"
              required
              value={form.name}
              onChange={set("name")}
              placeholder="Your full name"
              className={inputCls}
            />
          </div>

          <div className="sm:col-span-1">
            <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#8A8A8A]">
              Email *
            </label>
            <Input
              data-testid="booking-input-email"
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="you@venue.com"
              className={inputCls}
            />
          </div>

          <div className="sm:col-span-1">
            <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#8A8A8A]">
              Event Type
            </label>
            <select
              data-testid="booking-input-event-type"
              value={form.event_type}
              onChange={set("event_type")}
              className={`${inputCls} w-full appearance-none cursor-pointer`}
            >
              {EVENT_TYPES.map((t) => (
                <option key={t} value={t} className="bg-[#0A0A0A]">
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-1">
            <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#8A8A8A]">
              Event Date *
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="booking-input-date"
                  className={`${inputCls} w-full text-left flex items-center justify-between cursor-pointer`}
                >
                  <span className={form.event_date ? "text-white" : "text-[#5A5A5A]"}>
                    {form.event_date
                      ? new Date(form.event_date).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "Pick a date"}
                  </span>
                  <CalendarIcon size={14} className="text-[#8A8A8A]" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-auto p-0 rounded-none bg-[#0A0A0A] border border-white/10"
              >
                <Calendar
                  mode="single"
                  selected={form.event_date}
                  onSelect={(d) => set("event_date")(d)}
                  disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="sm:col-span-1">
            <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#8A8A8A]">
              Location *
            </label>
            <Input
              data-testid="booking-input-location"
              required
              value={form.location}
              onChange={set("location")}
              placeholder="City / Venue"
              className={inputCls}
            />
          </div>

          <div className="sm:col-span-1">
            <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#8A8A8A]">
              Budget (optional)
            </label>
            <Input
              data-testid="booking-input-budget"
              value={form.budget}
              onChange={set("budget")}
              placeholder="EUR / USD / INR"
              className={inputCls}
            />
          </div>

          <div className="sm:col-span-2">
            <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#8A8A8A]">
              Message
            </label>
            <Textarea
              data-testid="booking-input-message"
              value={form.message}
              onChange={set("message")}
              placeholder="Set length, vibe, lineup, anything else…"
              rows={4}
              className={`${inputCls} resize-none`}
            />
          </div>

          <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-2">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#5A5A5A]">
              {done ? "✓ Booking received — check your inbox." : "* Required"}
            </p>
            <button
              type="submit"
              data-testid="booking-submit"
              disabled={submitting}
              className="font-mono text-xs tracking-[0.25em] uppercase bg-[#FF2A2A] text-black px-10 py-4 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? "Sending…" : "Send Request →"}
            </button>
          </div>
        </form>
        </Reveal>
      </div>
    </section>
  );
}
